import { Container, Sprite } from 'pixi.js';
import type { INameTag } from '../IRenderer';
import { PixiOverlayHost } from '../PixiOverlayHost';
import type { Parkable } from '../pool/DisplayPool';
import { LabelAtlas, LABEL_ATLAS_RES, tokenizeLabel, type AtlasClient, type LabelFontStyle } from './LabelAtlas';

/**
 * SpriteLabel — a world label drawn as a run of sprites over the LabelAtlas
 * page (plan 16 §2.3). Changing "54" to "53" swaps frames of one texture
 * source, which Pixi 8 does without rebuilding the stage; a canvas `Text`
 * would re-rasterise and rebuild every time.
 *
 * Structure: root Container (position, alpha, scale) → glyph Container at
 * 1/RES → one Sprite per token slot. Slots only grow (a longer string adds a
 * sprite once); unused slots sit at alpha 0. Colour is the fill sprites'
 * tint. Hiding is by alpha, never `visible`. As a `Parkable` it can be pooled
 * (floating combat text): park = alpha 0, attached, at its depth.
 */

export interface SpriteLabelStyle extends LabelFontStyle {
  color: number;
  depth: number;
  anchorX: number;
  anchorY: number;
  roundPixels?: boolean;
}

export class SpriteLabel implements INameTag, Parkable, AtlasClient {
  private root: Container | null = null;
  private glyphs: Container | null = null;
  private slots: Sprite[] = [];
  private destroyed = false;
  private text: string;
  private x: number;
  private y: number;
  private alpha = 1;
  private shown = true;
  private scale = 1;
  private color: number;
  private readonly style: SpriteLabelStyle;

  constructor(x: number, y: number, text: string, style: SpriteLabelStyle) {
    this.x = x;
    this.y = y;
    this.text = text;
    this.style = style;
    this.color = style.color;
    const ownerStack = (globalThis as { __renderAttribLevel?: number }).__renderAttribLevel! >= 3
      ? new Error().stack : undefined;
    void PixiOverlayHost.ensureReady().then(() => {
      if (this.destroyed) return;
      const root = new Container();
      const glyphs = new Container();
      // Names for the perf probe (`--render-attrib 3`).
      root.label = 'SpriteLabel';
      glyphs.label = 'SpriteLabel';
      if (ownerStack) (root as Container & { __lvOwnerStack?: string }).__lvOwnerStack = ownerStack;
      glyphs.scale.set(1 / LABEL_ATLAS_RES, 1 / LABEL_ATLAS_RES);
      root.addChild(glyphs);
      root.zIndex = style.depth;
      this.root = root;
      this.glyphs = glyphs;
      this.applyTransform();
      this.relayout();
      LabelAtlas.register(this);
      PixiOverlayHost.addWorldChild(root);
    });
  }

  // ── INameTag ────────────────────────────────────────────────────────────

  setText(text: string): void {
    if (text === this.text) return;
    this.text = text;
    this.relayout();
  }

  setPosition(x: number, y: number): void {
    this.x = x;
    this.y = y;
    if (this.root) this.root.position.set(x, y);
  }

  setAlpha(alpha: number): void {
    this.alpha = alpha;
    if (this.root) this.root.alpha = this.shown ? alpha : 0;
  }

  /** Shown/hidden by alpha: a `visible` flip rebuilds the stage. */
  setVisible(visible: boolean): void {
    this.shown = visible;
    if (this.root) this.root.alpha = visible ? this.alpha : 0;
  }

  setScale(scale: number): void {
    this.scale = scale;
    if (this.root) this.root.scale.set(scale, scale);
  }

  setColor(color: number): void {
    if (color === this.color) return;
    this.color = color;
    for (const s of this.slots) s.tint = color;
  }

  // ── Parkable ────────────────────────────────────────────────────────────

  park(): void {
    this.setAlpha(0);
    this.setScale(1);
  }

  unpark(): void {
    this.shown = true;
    this.setAlpha(1);
  }

  destroy(): void {
    this.destroyed = true;
    LabelAtlas.unregister(this);
    this.root?.destroy({ children: true });
    this.root = null;
    this.glyphs = null;
    this.slots = [];
  }

  // ── Layout ──────────────────────────────────────────────────────────────

  /** Point the slots at the frames of the current text (atlas client hook). */
  relayout(): void {
    const glyphs = this.glyphs;
    if (!glyphs) return;
    const tokens = tokenizeLabel(this.text);
    const frames = tokens.map((t) => LabelAtlas.frame(t, this.style));
    while (this.slots.length < frames.length) {
      const s = new Sprite(LabelAtlas.blank());
      s.tint = this.color;
      s.alpha = 0;
      if (this.style.roundPixels) s.roundPixels = true;
      glyphs.addChild(s);
      this.slots.push(s);
    }
    // Lay out like a canvas Text: pens advance by the text's own width and
    // the stroke overhangs once at each end of the whole label.
    const R = LABEL_ATLAS_RES;
    const pad = frames.length > 0 ? frames[0].pad : 0;
    let width = 2 * pad;
    let height = 0;
    for (const f of frames) {
      width += f.advance;
      height = Math.max(height, f.height);
    }
    let pen = -width * this.style.anchorX + pad;
    const top = -height * this.style.anchorY * R;
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i];
      const f = frames[i];
      if (!f) {
        s.alpha = 0;
        continue;
      }
      if (s.texture !== f.texture) s.texture = f.texture;
      s.width = f.width * R;
      s.height = f.height * R;
      s.position.set(Math.round((pen - f.pad) * R), top);
      s.alpha = 1;
      pen += f.advance;
    }
  }

  private applyTransform(): void {
    const root = this.root;
    if (!root) return;
    root.position.set(this.x, this.y);
    root.alpha = this.shown ? this.alpha : 0;
    root.scale.set(this.scale, this.scale);
  }
}
